import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/LegalPages.css';

const PrivacyPolicy = () => {
  return (
    <div className="legal-page-container">
      <div className="legal-content">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Effective Date: January 2, 2026</p>

        <p>
          Thank you for using Yesod. Your privacy is very important to us. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the Yesod mobile application (the \"App\") and our web services. This includes our features for prayer management, community connection, and daily halachic times.
        </p>

        <p>
          <strong>By using the App, you agree to the terms of this Privacy Policy.</strong>
        </p>

        <section>
          <h2>1. Information We Collect</h2>
          <p>We collect the following types of information:</p>
          
          <h3>a. Location Information</h3>
          <ul>
            <li>We collect your precise or approximate location (with your permission) to provide accurate zmanim, candle lighting times, and to calculate \"Sunset/Sunrise\" for prayer accuracy.</li>
            <li>You may also manually select a different location in the app.</li>
          </ul>

          <h3>b. Personal Information</h3>
          <ul>
            <li><strong>Account Data:</strong> When you register, we collect your name, email address, and phone number.</li>
            <li><strong>Prayer Data:</strong> We store the custom lists you create (e.g., \"My Family,\" \"Soldiers\"), including the names of individuals you add to these lists.</li>
            <li><strong>Group Data:</strong> If you create or join a prayer group, we collect information about your membership and contribution to the group's prayer goals (e.g., chapters read).</li>
          </ul>

          <h3>c. Usage Data</h3>
          <p>We collect anonymized usage statistics such as app screens viewed, prayer modes used (Daily vs. Monthly), tap events, time spent on the app, and crash reports.</p>

          <h3>d. AI Assistant Usage Data</h3>
          <p>When you use our AI Assistant features (currently in BETA), we collect prompts, requests, and AI-generated responses to improve model accuracy and troubleshoot issues.</p>
          <p><strong>Note:</strong> Do not submit sensitive personal information to the AI Assistant.</p>

          <h3>e. Device Information</h3>
          <p>We may collect device-specific information such as operating system, device model, and unique identifiers (such as IP address) for analytics and troubleshooting.</p>
        </section>

        <section>
          <h2>2. How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul>
            <li>Provide you with accurate zmanim and religious times based on your location.</li>
            <li><strong>Sync your Prayer Lists:</strong> Ensure your lists and progress are backed up and available across your devices.</li>
            <li><strong>Facilitate Sharing:</strong> Allow you to share lists via links. When you generate a share link, we use the data to construct a preview for the recipient.</li>
            <li>Allow you to connect with and receive information from your selected community (events, classes, messages).</li>
            <li>Improve the app's features, usability, and performance.</li>
            <li>Ensure compliance with applicable legal requirements and platform policies.</li>
          </ul>
        </section>

        <section>
          <h2>3. Sharing of Information</h2>
          <p>We do not sell or rent your personal information. We may share data in the following ways:</p>
          <ul>
            <li><strong>Publicly Shared Links:</strong> If you voluntarily use the \"Share List\" feature, a public web link is generated containing the names in that specific list. Anyone with the link can view those names and import them. You are responsible for the privacy of the names you choose to share.</li>
            <li><strong>With your chosen community:</strong> When you connect to a synagogue, your name and email may be shared with that community administrator.</li>
            <li><strong>With service providers:</strong> We may share data with third-party vendors for analytics (e.g., Google Firebase, Apple Analytics), crash reporting, and cloud infrastructure.</li>
            <li><strong>For legal reasons:</strong> If required by law, regulation, or legal process.</li>
          </ul>
        </section>

        <section>
          <h2>4. Data Storage and Security</h2>
          <ul>
            <li>All data is stored securely using industry-standard encryption methods.</li>
            <li>We use cloud services such as Firebase and Supabase, which comply with privacy and security regulations.</li>
            <li>Access to user data is restricted and monitored for security.</li>
          </ul>
        </section>

        <section>
          <h2>5. User Control and Choices</h2>
          <p>You have the following rights:</p>
          <ul>
            <li><strong>Location Access:</strong> You can enable or disable location permissions at any time in your device settings.</li>
            <li><strong>Prayer Data:</strong> You can edit or delete names from your lists at any time. Deleting a name removes it from your view; however, if you have previously shared a list via link, recipients who already imported that data will still retain their copy.</li>
            <li><strong>Account Deletion:</strong> You may request deletion of your account and all associated data by contacting us at shalom@yesod.us.</li>
          </ul>
        </section>

        <section>
          <h2>6. Children's Privacy</h2>
          <p>
            The Yesod App is not intended for children under 13. We do not knowingly collect personal information from children. If we learn we have collected such data, we will delete it promptly.
          </p>
        </section>

        <section>
          <h2>7. Third-Party Links and Services</h2>
          <p>
            The App may contain links to third-party content or services. We are not responsible for their privacy practices. We recommend reviewing their privacy policies independently.
          </p>
        </section>

        <section>
          <h2>8. Changes to this Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. You will be notified of any material changes via the app or email.
          </p>
        </section>

        <section>
          <h2>9. Contact Us</h2>
          <p>
            If you have questions or concerns about this Privacy Policy or how your data is handled, contact us at:
          </p>
          <p>
            <strong>Yesod App</strong><br/>
            Email: shalom@yesod.us<br/>
            Website: www.yesod.us
          </p>
        </section>

      </div>
    </div>
  );
};

export default PrivacyPolicy;